'use client';

import { useSendSurvey } from '@/api/mutation/use-send-survey';
import type { CustomTranslateKey } from '@/basket/types/i18n';
import Button from '@/components/base/button/button';
import { SURVEY_QUESTIONS } from '@/components/base/customer-survey/constants';
import { SurveyQuestion } from '@/components/base/customer-survey/survey-question';
import PhosphorIcon from '@/components/base/icon/phosphor-icon';
import Modal from '@/components/base/modal/modal';
import { useAddToast } from '@/components/base/toast/use-toast';
import { useCustomer } from '@/hooks/use-customer';
import {
  SurveyModalStore,
  useSurveyModalStore,
} from '@/store/survey-modal-store';
import type { Entries } from '@/types/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { Heart } from '@phosphor-icons/react';
import { useTranslations } from 'next-intl';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

const defaultValues = {
  achievement: '',
  satisfaction: '',
  feedback: '',
};

const schema = z.object({
  achievement: z.string().min(1, 'error.select_an_option'),
  satisfaction: z.string().min(1, 'error.select_an_option'),
  feedback: z.string().optional(),
});

export type SurveyFormValues = z.infer<typeof schema>;

const selector = ({ isOpen, submitSurvey, closeModal }: SurveyModalStore) => ({
  isOpen,
  closeModal,
  submitSurvey,
});

export const CustomerSurvey = () => {
  const t = useTranslations('common.survey');
  const form = useForm<SurveyFormValues>({
    defaultValues,
    resolver: zodResolver(schema),
  });
  const email = useCustomer()?.email;

  const toast = useAddToast();

  const { mutateAsync, isPending } = useSendSurvey();

  const { submitSurvey, isOpen, closeModal } = useSurveyModalStore(selector);

  const isModalOpen = isOpen || false;

  const onSubmit = async (values: SurveyFormValues) => {
    const convertedAnswers = (
      Object.entries(values) as Entries<typeof values>
    ).map(([key, value]) => {
      const isAnswerWithOptions = key !== 'feedback';

      return {
        question: t(`${key}.question`),
        answer: isAnswerWithOptions
          ? t(`${key}.options.${value}` as CustomTranslateKey)
          : value,
      };
    });
    await mutateAsync({
      answers: convertedAnswers,
      email,
    });

    toast({
      type: 'success',
      content: t('success_send_toast'),
    });
    submitSurvey();
  };

  const isFormSubmitting = isPending || form.formState.isSubmitting;

  return (
    <Modal
      isOpen={isModalOpen}
      title={t('title')}
      titleClassName="py-xxs"
      setIsOpen={closeModal}
      className="w-full max-w-modal-max-width-form"
      closeOnBackdropClick={false}
      testId={'customer-survey-modal'}
      wrapperClassName="!overflow-y-hidden"
      footer={
        <div className="flex w-full s:justify-end">
          <Button
            className="s:w-auto w-full"
            variant="primary"
            type="submit"
            testId={'submit-survey'}
            isLoading={isFormSubmitting}
            onClick={form.handleSubmit(onSubmit)}
          >
            {t('btn_send_answers')}
          </Button>
        </div>
      }
    >
      <div className="relative flex h-full w-full">
        <FormProvider {...form}>
          <div className="pointer-events-none absolute bottom-0 bottom-shadow z-10 customer-survey:hidden h-[70px] w-full"></div>
          <div className="flex flex-col gap-m overflow-y-auto p-inner pb-[50px]">
            <div className="flex flex-col">
              <div className="flex items-center gap-xxs">
                <PhosphorIcon Icon={Heart} className="icon-m text-brandMain" />
                <p className="text-default text-large">{t('heading')}</p>
              </div>
              <p className="mt-xxs text-body text-default">
                {t('description')}{' '}
              </p>
            </div>

            {SURVEY_QUESTIONS.map((question, index) => (
              <SurveyQuestion
                key={question.name}
                {...question}
                testId={`survey-question-${index + 1}`}
              />
            ))}
          </div>
        </FormProvider>
      </div>
    </Modal>
  );
};
